import React, { useState, useEffect } from 'react';
import CodeOfConductModal from './CodeOfConductModal';
import { EntryRequirementNotice, EntryRequirementCheckbox } from './EntryRequirementNotice';
import { relationshipTypesApi, RelationshipType } from '../api/client';

export interface TicketFormData {
  given_name: string;
  family_name: string;
  pronouns: string;
  email: string;
  company: string;
  role: string;
  phone: string;
  linkedin_url: string;
  relationship_type: string;
  dietary_allergies: string;
  dietary_preferences: string;
  non_dietary_requirements: string;
  how_heard: string;
  code_of_conduct_accepted: boolean;
  privacy_policy_accepted: boolean;
  entry_requirement_accepted?: boolean;
  newsletter_opt_in: boolean;
}

interface TicketRequestFormProps {
  formData: TicketFormData;
  onChange: (data: TicketFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitButtonText?: string;
  isSubmitting?: boolean;
  showPrivacyPolicy?: boolean;
  autoFocus?: boolean;
  entryRequirement?: string;
}

export const TicketRequestForm: React.FC<TicketRequestFormProps> = ({
  formData,
  onChange,
  onSubmit,
  submitButtonText = 'Request Ticket',
  isSubmitting = false,
  showPrivacyPolicy = true,
  autoFocus = false,
  entryRequirement,
}) => {
  const [showCodeOfConduct, setShowCodeOfConduct] = useState(false);
  const [hasDietaryRequirements, setHasDietaryRequirements] = useState(false);
  const [hasNonDietaryRequirements, setHasNonDietaryRequirements] = useState(false);
  const [dietaryCheckboxes, setDietaryCheckboxes] = useState({
    allergies: false,
    preferences: false,
  });
  const [relationshipTypes, setRelationshipTypes] = useState<RelationshipType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    loadRelationshipTypes();
  }, []);

  const loadRelationshipTypes = async () => {
    try {
      const types = await relationshipTypesApi.getRelationshipTypes();
      // Only show active types
      setRelationshipTypes(types.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load relationship types:', error);
      // Fallback to default types if API fails
      setRelationshipTypes([
        { id: 1, organization_id: 1, name: 'existing_client', display_name: 'Existing client', is_active: true, created_at: '', updated_at: '' },
        { id: 2, organization_id: 1, name: 'prospective_client', display_name: 'Prospective client', is_active: true, created_at: '', updated_at: '' },
        { id: 3, organization_id: 1, name: 'partner', display_name: 'Partner', is_active: true, created_at: '', updated_at: '' },
        { id: 4, organization_id: 1, name: 'staff', display_name: 'Staff', is_active: true, created_at: '', updated_at: '' }
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleFieldChange = (field: keyof TicketFormData, value: any) => {
    onChange({ ...formData, [field]: value });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {entryRequirement && <EntryRequirementNotice text={entryRequirement} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="given_name" className="block text-sm font-medium text-gray-700 mb-1">
            Given name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="given_name"
            required
            value={formData.given_name}
            onChange={(e) => handleFieldChange('given_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus={autoFocus}
          />
        </div>

        <div>
          <label htmlFor="family_name" className="block text-sm font-medium text-gray-700 mb-1">
            Family name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="family_name"
            required
            value={formData.family_name}
            onChange={(e) => handleFieldChange('family_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor="pronouns" className="block text-sm font-medium text-gray-700 mb-1">
          Personal Pronouns
        </label>
        <select
          id="pronouns"
          value={formData.pronouns}
          onChange={(e) => handleFieldChange('pronouns', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Please choose</option>
          <option value="he/him/his">he/him/his (will show on badge)</option>
          <option value="she/her/hers">she/her/hers (will show on badge)</option>
          <option value="they/them/theirs">they/them/theirs (will show on badge)</option>
          <option value="prefer not to say">Prefer not to say (nothing shown on badge)</option>
        </select>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Attendee email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          required
          value={formData.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
            Company <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="company"
            required
            value={formData.company}
            onChange={(e) => handleFieldChange('company', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
            Role <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="role"
            required
            value={formData.role}
            onChange={(e) => handleFieldChange('role', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            required
            value={formData.phone}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-700 mb-1">
            LinkedIn profile <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            id="linkedin_url"
            placeholder="https://www.linkedin.com/in/yourname"
            value={formData.linkedin_url}
            onChange={(e) => handleFieldChange('linkedin_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor="relationship_type" className="block text-sm font-medium text-gray-700 mb-1">
          Attendee type <span className="text-red-500">*</span>
        </label>
        <select
          id="relationship_type"
          required
          value={formData.relationship_type}
          onChange={(e) => handleFieldChange('relationship_type', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loadingTypes}
        >
          <option value="">Please choose</option>
          {relationshipTypes.map((type) => (
            <option key={type.id} value={type.name}>
              {type.display_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Do you have any dietary requirements?
        </label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="dietary_requirements"
              value="yes"
              checked={hasDietaryRequirements === true}
              onChange={() => setHasDietaryRequirements(true)}
              className="mr-2"
            />
            Yes
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="dietary_requirements"
              value="no"
              checked={hasDietaryRequirements === false}
              onChange={() => {
                setHasDietaryRequirements(false);
                setDietaryCheckboxes({ allergies: false, preferences: false });
                handleFieldChange('dietary_allergies', '');
                handleFieldChange('dietary_preferences', '');
              }}
              className="mr-2"
            />
            No
          </label>
        </div>
        
        {hasDietaryRequirements && (
          <div className="mt-3 ml-4 space-y-3">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={dietaryCheckboxes.allergies}
                  onChange={(e) => {
                    setDietaryCheckboxes({ ...dietaryCheckboxes, allergies: e.target.checked });
                    if (!e.target.checked) {
                      handleFieldChange('dietary_allergies', '');
                    }
                  }}
                  className="mr-2"
                />
                Allergies/Intolerances
              </label>
              {dietaryCheckboxes.allergies && (
                <input
                  type="text"
                  value={formData.dietary_allergies}
                  onChange={(e) => handleFieldChange('dietary_allergies', e.target.value)}
                  placeholder="Please specify the allergies / intolerances"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={dietaryCheckboxes.preferences}
                  onChange={(e) => {
                    setDietaryCheckboxes({ ...dietaryCheckboxes, preferences: e.target.checked });
                    if (!e.target.checked) {
                      handleFieldChange('dietary_preferences', '');
                    }
                  }}
                  className="mr-2"
                />
                Dietary preferences
              </label>
              {dietaryCheckboxes.preferences && (
                <input
                  type="text"
                  value={formData.dietary_preferences}
                  onChange={(e) => handleFieldChange('dietary_preferences', e.target.value)}
                  placeholder="Please specify your dietary preferences (e.g., vegetarian, vegan, halal)"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Do you have any other non-dietary requirements?
        </label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="non_dietary_requirements"
              value="yes"
              checked={hasNonDietaryRequirements === true}
              onChange={() => setHasNonDietaryRequirements(true)}
              className="mr-2"
            />
            Yes
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="non_dietary_requirements"
              value="no"
              checked={hasNonDietaryRequirements === false}
              onChange={() => {
                setHasNonDietaryRequirements(false);
                handleFieldChange('non_dietary_requirements', '');
              }}
              className="mr-2"
            />
            No
          </label>
        </div>
        
        {hasNonDietaryRequirements && (
          <textarea
            value={formData.non_dietary_requirements}
            onChange={(e) => handleFieldChange('non_dietary_requirements', e.target.value)}
            placeholder="Please specify your requirements"
            rows={3}
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>

      <div>
        <label htmlFor="how_heard" className="block text-sm font-medium text-gray-700 mb-1">
          How did you hear about this event?
        </label>
        <input
          type="text"
          id="how_heard"
          value={formData.how_heard}
          onChange={(e) => handleFieldChange('how_heard', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {entryRequirement && (
        <EntryRequirementCheckbox
          id="entry_requirement"
          checked={!!formData.entry_requirement_accepted}
          onChange={(v) => handleFieldChange('entry_requirement_accepted', v)}
        />
      )}

      <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={formData.code_of_conduct_accepted}
            onChange={(e) => handleFieldChange('code_of_conduct_accepted', e.target.checked)}
            required
            className="mr-2 mt-1"
          />
          <span className="text-sm">
            I have read and agree to the{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowCodeOfConduct(true);
              }}
              className="text-blue-600 hover:underline"
            >
              Code of Conduct
            </button>
            {' '}<span className="text-red-500">*</span>
          </span>
        </label>
      </div>

      {showPrivacyPolicy && (
        <div className="border border-gray-300 rounded-md p-4 bg-gray-50 mt-4">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.privacy_policy_accepted}
              onChange={(e) => handleFieldChange('privacy_policy_accepted', e.target.checked)}
              required
              className="mr-2 mt-1"
            />
            <span className="text-sm">
              I have read and agree to the{' '}
              <a
                href="https://star.london/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Privacy Policy
              </a>
              {' '}<span className="text-red-500">*</span>
            </span>
          </label>
        </div>
      )}

      <div className="bg-gray-50 rounded-md p-4">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={formData.newsletter_opt_in}
            onChange={(e) => handleFieldChange('newsletter_opt_in', e.target.checked)}
            className="mr-2 mt-1"
          />
          <span className="text-sm text-gray-700">
            I would like to receive updates about future events, priority invites, and news from Star London
          </span>
        </label>
      </div>

      <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-md">
        <p className="font-medium mb-1">This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Privacy Policy
          </a> and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Terms of Service
          </a> apply.
        </p>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
      >
        {isSubmitting ? 'Submitting...' : submitButtonText}
      </button>

      <CodeOfConductModal 
        isOpen={showCodeOfConduct}
        onClose={() => setShowCodeOfConduct(false)}
      />
    </form>
  );
};

export default TicketRequestForm;